import React, { useState, useCallback, useMemo, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { useDropzone } from "react-dropzone";
import JSZip from "jszip";
import html2canvas from "html2canvas";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

const SessionLengthAnalyzer = ({ isAdmin }) => {
  const chartContainerRef = useRef(null);
  const [sessionData1, setSessionData1] = useState(null);
  const [sessionData2, setSessionData2] = useState(null);
  const [uploadedFiles1, setUploadedFiles1] = useState([]);
  const [uploadedFiles2, setUploadedFiles2] = useState([]);
  const [isProcessing1, setIsProcessing1] = useState(false);
  const [isProcessing2, setIsProcessing2] = useState(false);
  const [notification, setNotification] = useState(null);
  const [mapName, setMapName] = useState("");
  const [showNamePrompt, setShowNamePrompt] = useState(false);
  const [maxSessionTime, setMaxSessionTime] = useState(90);
  const [dataset1Name, setDataset1Name] = useState("Dataset 1");
  const [dataset2Name, setDataset2Name] = useState("Dataset 2");

  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const parseSessionLengthCSV = (content) => {
    const lines = content.split("\n").filter((line) => line.trim());
    console.log(`Parsing session length CSV with ${lines.length} lines`);

    // Skip header row and parse data
    const data = lines
      .slice(1)
      .map((line, index) => {
        // Handle CSV parsing - split by comma but handle quoted values
        const values = line
          .split(",")
          .map((val) => val.trim().replace(/"/g, ""));

        let sessionTime, sessions;

        if (values.length === 2) {
          // Normal case: sessionTime, sessions
          sessionTime = parseFloat(values[0]) || 0;
          sessions = parseInt(values[1].replace(/,/g, ""), 10) || 0;
        } else if (values.length === 3) {
          // Case where sessions has comma separator: sessionTime, first_part, second_part
          sessionTime = parseFloat(values[0]) || 0;
          // Combine parts 1 and 2 to form the full number
          const fullNumber = values[1] + values[2];
          sessions = parseInt(fullNumber, 10) || 0;
        } else if (values.length >= 2) {
          // Fallback for other cases
          sessionTime = parseFloat(values[0]) || 0;
          sessions = parseInt(values[1].replace(/,/g, ""), 10) || 0;
        } else {
          console.warn(`Unexpected CSV format on line ${index + 2}:`, values);
          return null;
        }

        console.log(
          `Line ${
            index + 2
          }: sessionTime=${sessionTime}, sessions=${sessions}, raw values:`,
          values
        );

        return {
          sessionTime,
          sessions,
        };
      })
      .filter((item) => item !== null && item.sessions > 0)
      .sort((a, b) => a.sessionTime - b.sessionTime); // Sort by session time

    console.log(
      `Successfully parsed ${data.length} session data points:`,
      data
    );

    // Log total sessions for verification
    const totalParsed = data.reduce((sum, item) => sum + item.sessions, 0);
    console.log(`Total sessions parsed: ${totalParsed}`);

    return data;
  };

  const processDataset = (sessionData) => {
    if (!sessionData) return null;

    // Apply session time filter and group sessions
    // Create a deep copy of the original data to avoid mutations
    const filteredData = [];
    let combinedSessions = 0;

    sessionData.forEach((item) => {
      if (item.sessionTime <= maxSessionTime) {
        // Create a copy of the item to avoid mutating original data
        filteredData.push({
          sessionTime: item.sessionTime,
          sessions: item.sessions,
        });
      } else {
        // Combine all sessions beyond maxSessionTime
        combinedSessions += item.sessions;
      }
    });

    // Add the combined sessions at maxSessionTime if there are any
    if (combinedSessions > 0) {
      // Check if we already have data at maxSessionTime
      const existingIndex = filteredData.findIndex(
        (item) => item.sessionTime === maxSessionTime
      );
      if (existingIndex >= 0) {
        // Create a new object instead of mutating the existing one
        filteredData[existingIndex] = {
          sessionTime: filteredData[existingIndex].sessionTime,
          sessions: filteredData[existingIndex].sessions + combinedSessions,
        };
      } else {
        filteredData.push({
          sessionTime: maxSessionTime,
          sessions: combinedSessions,
        });
      }
    }

    // Sort by session time
    filteredData.sort((a, b) => a.sessionTime - b.sessionTime);

    // Calculate total sessions
    const totalSessions = filteredData.reduce(
      (sum, item) => sum + item.sessions,
      0
    );

    // Calculate remaining players percentage
    // At minute X, we show the percentage AFTER removing those who left at minute X
    let cumulativeSessions = 0;
    const processedData = filteredData.map((item, index) => {
      cumulativeSessions += item.sessions;
      const remainingPlayers =
        ((totalSessions - cumulativeSessions) / totalSessions) * 100;

      return {
        sessionTime: item.sessionTime,
        sessions: item.sessions,
        remainingPlayersPercent: Math.max(0, remainingPlayers),
        totalSessions,
      };
    });

    // Calculate net change for each data point
    const dataWithNetChange = processedData.map((item, index) => {
      let netChange = 0;
      if (index > 0) {
        const previousPercent =
          processedData[index - 1].remainingPlayersPercent;
        netChange = item.remainingPlayersPercent - previousPercent;
      }

      return {
        ...item,
        netChange: netChange,
      };
    });

    // Add initial point at 0 minutes with 100% remaining only if the first data point isn't at minute 0
    const firstDataPoint = dataWithNetChange[0];
    if (firstDataPoint && firstDataPoint.sessionTime > 0) {
      const finalData = [
        {
          sessionTime: 0,
          sessions: 0,
          remainingPlayersPercent: 100,
          totalSessions,
          netChange: 0,
        },
        ...dataWithNetChange,
      ];

      // Recalculate net change for the first actual data point
      if (finalData.length > 1) {
        finalData[1].netChange = finalData[1].remainingPlayersPercent - 100;
      }

      return finalData;
    }

    // If data starts at minute 0, we need to adjust the first point to show 100% before any sessions end
    if (firstDataPoint && firstDataPoint.sessionTime === 0) {
      dataWithNetChange[0].remainingPlayersPercent = 100;
      dataWithNetChange[0].netChange = 0;
    }

    return dataWithNetChange;
  };

  const processedData1 = useMemo(
    () => processDataset(sessionData1),
    [sessionData1, maxSessionTime, processDataset]
  );
  const processedData2 = useMemo(
    () => processDataset(sessionData2),
    [sessionData2, maxSessionTime, processDataset]
  );

  const chartData = useMemo(() => {
    if (!processedData1 && !processedData2) return null;

    // Create a unified set of labels from both datasets
    const allTimePoints = new Set();
    if (processedData1) {
      processedData1.forEach((item) => allTimePoints.add(item.sessionTime));
    }
    if (processedData2) {
      processedData2.forEach((item) => allTimePoints.add(item.sessionTime));
    }

    const sortedTimePoints = Array.from(allTimePoints).sort((a, b) => a - b);
    const labels = sortedTimePoints.map((time) => {
      if (
        time === maxSessionTime &&
        ((sessionData1 &&
          sessionData1.some((s) => s.sessionTime > maxSessionTime)) ||
          (sessionData2 &&
            sessionData2.some((s) => s.sessionTime > maxSessionTime)))
      ) {
        return `${time}+ min`;
      }
      return `${time} min`;
    });

    const datasets = [];

    if (processedData1) {
      // Create data array for dataset 1, filling gaps with null
      const data1 = sortedTimePoints.map((time) => {
        const dataPoint = processedData1.find(
          (item) => item.sessionTime === time
        );
        return dataPoint ? dataPoint.remainingPlayersPercent : null;
      });

      datasets.push({
        label: `${dataset1Name} - Remaining Players (%)`,
        data: data1,
        borderColor: "#10B981",
        backgroundColor: "rgba(16, 185, 129, 0.1)",
        fill: false,
        tension: 0.4,
        pointBackgroundColor: "#10B981",
        pointBorderColor: "#10B981",
        pointRadius: 4,
        pointHoverRadius: 6,
        spanGaps: true,
      });
    }

    if (processedData2) {
      // Create data array for dataset 2, filling gaps with null
      const data2 = sortedTimePoints.map((time) => {
        const dataPoint = processedData2.find(
          (item) => item.sessionTime === time
        );
        return dataPoint ? dataPoint.remainingPlayersPercent : null;
      });

      datasets.push({
        label: `${dataset2Name} - Remaining Players (%)`,
        data: data2,
        borderColor: "#EF4444",
        backgroundColor: "rgba(239, 68, 68, 0.1)",
        fill: false,
        tension: 0.4,
        pointBackgroundColor: "#EF4444",
        pointBorderColor: "#EF4444",
        pointRadius: 4,
        pointHoverRadius: 6,
        spanGaps: true,
      });
    }

    return {
      labels,
      datasets,
    };
  }, [
    processedData1,
    processedData2,
    maxSessionTime,
    sessionData1,
    sessionData2,
    dataset1Name,
    dataset2Name,
  ]);

  const processFile = useCallback(
    async (file, datasetNumber) => {
      const setIsProcessing =
        datasetNumber === 1 ? setIsProcessing1 : setIsProcessing2;
      const setUploadedFiles =
        datasetNumber === 1 ? setUploadedFiles1 : setUploadedFiles2;
      const setSessionData =
        datasetNumber === 1 ? setSessionData1 : setSessionData2;
      const uploadedFiles =
        datasetNumber === 1 ? uploadedFiles1 : uploadedFiles2;

      setIsProcessing(true);
      const newFiles = [...uploadedFiles];

      if (file.type === "application/zip" || file.name.endsWith(".zip")) {
        try {
          const reader = new FileReader();

          reader.onload = async () => {
            try {
              const zip = new JSZip();
              const zipContent = await zip.loadAsync(reader.result);

              // Look for session_length folder and session_length.csv file
              let sessionLengthFile = null;

              zipContent.forEach((relativePath, zipEntry) => {
                if (
                  !zipEntry.dir &&
                  (relativePath === "session_length/session_length.csv" ||
                    relativePath === "session_length\\session_length.csv")
                ) {
                  sessionLengthFile = zipEntry;
                }
              });

              if (!sessionLengthFile) {
                newFiles.push({
                  name: file.name,
                  size: formatBytes(file.size),
                  type: "zip",
                  processed: false,
                  error: "session_length/session_length.csv not found in ZIP",
                });

                setUploadedFiles(newFiles);
                setIsProcessing(false);
                showNotification(
                  "Could not find session_length/session_length.csv in the ZIP file",
                  "error"
                );
                return;
              }

              // Extract and process the session length CSV
              const csvContent = await sessionLengthFile.async("text");
              const parsedData = parseSessionLengthCSV(csvContent);

              if (parsedData.length > 0) {
                setSessionData(parsedData);

                newFiles.push({
                  name: file.name,
                  size: formatBytes(file.size),
                  type: "session_length",
                  processed: true,
                });

                showNotification(
                  `Successfully processed Dataset ${datasetNumber} with ${parsedData.length} data points`
                );
              } else {
                newFiles.push({
                  name: file.name,
                  size: formatBytes(file.size),
                  type: "zip",
                  processed: false,
                  error: "No valid session data found in CSV",
                });

                showNotification(
                  "No valid session data found in the CSV file",
                  "error"
                );
              }

              setUploadedFiles(newFiles);
              setIsProcessing(false);
            } catch (err) {
              console.error("Error processing ZIP file:", err);
              newFiles.push({
                name: file.name,
                size: formatBytes(file.size),
                type: "zip",
                processed: false,
                error: "Failed to process ZIP file",
              });

              setUploadedFiles(newFiles);
              setIsProcessing(false);
              showNotification("Failed to process ZIP file", "error");
            }
          };

          reader.readAsArrayBuffer(file);
        } catch (err) {
          console.error("Error reading file:", err);
          newFiles.push({
            name: file.name,
            size: formatBytes(file.size),
            type: "zip",
            processed: false,
            error: "Failed to read file",
          });

          setUploadedFiles(newFiles);
          setIsProcessing(false);
          showNotification("Failed to read file", "error");
        }
      } else {
        newFiles.push({
          name: file.name,
          size: formatBytes(file.size),
          type: "unknown",
          processed: false,
          error: "Only ZIP files are supported",
        });

        setUploadedFiles(newFiles);
        setIsProcessing(false);
        showNotification("Only ZIP files are supported", "error");
      }
    },
    [uploadedFiles1, uploadedFiles2, parseSessionLengthCSV, showNotification]
  );

  const onDrop1 = useCallback(
    (acceptedFiles) => {
      acceptedFiles.forEach((file) => processFile(file, 1));
    },
    [processFile]
  );

  const onDrop2 = useCallback(
    (acceptedFiles) => {
      acceptedFiles.forEach((file) => processFile(file, 2));
    },
    [processFile]
  );

  const {
    getRootProps: getRootProps1,
    getInputProps: getInputProps1,
    isDragActive: isDragActive1,
  } = useDropzone({
    onDrop: onDrop1,
    accept: {
      "application/zip": [".zip"],
    },
  });

  const {
    getRootProps: getRootProps2,
    getInputProps: getInputProps2,
    isDragActive: isDragActive2,
  } = useDropzone({
    onDrop: onDrop2,
    accept: {
      "application/zip": [".zip"],
    },
  });

  const removeFile = (index, datasetNumber) => {
    if (datasetNumber === 1) {
      const newFiles = [...uploadedFiles1];
      newFiles.splice(index, 1);
      setUploadedFiles1(newFiles);
      setSessionData1(null);
    } else {
      const newFiles = [...uploadedFiles2];
      newFiles.splice(index, 1);
      setUploadedFiles2(newFiles);
      setSessionData2(null);
    }
  };

  const handleSaveClick = () => {
    setShowNamePrompt(true);
  };

  const handleSaveImage = async (e) => {
    e?.preventDefault();
    if (!chartContainerRef.current || !mapName.trim()) return;

    try {
      const canvas = await html2canvas(chartContainerRef.current, {
        backgroundColor: "#1F2937",
        scale: 2,
        logging: false,
        useCORS: true,
      });

      const link = document.createElement("a");
      const date = new Date().toISOString().split("T")[0];
      link.download = `${mapName.trim()}-session-length-${date}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();

      setShowNamePrompt(false);
      setMapName("");
    } catch (error) {
      console.error("Error saving image:", error);
    }
  };

  return (
    <div className="space-y-6">
      {notification && (
        <div
          className={`px-4 py-3 rounded relative ${
            notification.type === "error"
              ? "bg-red-900 text-red-100 border border-red-800"
              : "bg-green-900 text-green-100 border border-green-800"
          }`}
          role="alert"
        >
          <span className="block sm:inline">{notification.message}</span>
          <button
            type="button"
            className="absolute top-0 bottom-0 right-0 px-4"
            onClick={() => setNotification(null)}
          >
            <span className="sr-only">Close</span>
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      )}

      {/* Dataset Name Inputs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Dataset 1 Name:
          </label>
          <input
            type="text"
            value={dataset1Name}
            onChange={(e) => setDataset1Name(e.target.value)}
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter name for first dataset"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Dataset 2 Name:
          </label>
          <input
            type="text"
            value={dataset2Name}
            onChange={(e) => setDataset2Name(e.target.value)}
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter name for second dataset"
          />
        </div>
      </div>

      {/* Dual Drop Zones */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Dataset 1 Drop Zone */}
        <div>
          <h3 className="text-lg font-medium text-gray-200 mb-3">
            {dataset1Name}
          </h3>
          <div
            {...getRootProps1()}
            className={`p-6 border-2 border-dashed rounded-lg text-center cursor-pointer transition-colors
              ${
                isDragActive1
                  ? "border-green-500 bg-green-50/10"
                  : "border-gray-600 hover:border-gray-500"
              }
              ${isProcessing1 ? "opacity-50 pointer-events-none" : ""}`}
          >
            <input {...getInputProps1()} />
            <div className="flex flex-col items-center justify-center">
              <svg
                className="w-10 h-10 text-gray-400 mb-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>

              <p className="text-gray-300 mb-2 text-sm">
                {isDragActive1
                  ? "Drop ZIP file here..."
                  : "Drag & drop ZIP file here"}
              </p>
              <p className="text-xs text-gray-500">
                session_length/session_length.csv
              </p>

              {isProcessing1 && (
                <div className="mt-3">
                  <div className="animate-pulse text-green-400 flex items-center text-sm">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-3 w-3 text-green-500"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Processing...
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Dataset 2 Drop Zone */}
        <div>
          <h3 className="text-lg font-medium text-gray-200 mb-3">
            {dataset2Name}
          </h3>
          <div
            {...getRootProps2()}
            className={`p-6 border-2 border-dashed rounded-lg text-center cursor-pointer transition-colors
              ${
                isDragActive2
                  ? "border-red-500 bg-red-50/10"
                  : "border-gray-600 hover:border-gray-500"
              }
              ${isProcessing2 ? "opacity-50 pointer-events-none" : ""}`}
          >
            <input {...getInputProps2()} />
            <div className="flex flex-col items-center justify-center">
              <svg
                className="w-10 h-10 text-gray-400 mb-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>

              <p className="text-gray-300 mb-2 text-sm">
                {isDragActive2
                  ? "Drop ZIP file here..."
                  : "Drag & drop ZIP file here"}
              </p>
              <p className="text-xs text-gray-500">
                session_length/session_length.csv
              </p>

              {isProcessing2 && (
                <div className="mt-3">
                  <div className="animate-pulse text-red-400 flex items-center text-sm">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-3 w-3 text-red-500"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Processing...
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {(uploadedFiles1.length > 0 || uploadedFiles2.length > 0) && (
        <div className="overflow-hidden rounded-lg bg-gray-800 shadow">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-200 mb-4">
              Uploaded Files
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Dataset 1 Files */}
              <div>
                <h4 className="text-md font-medium text-green-400 mb-3">
                  {dataset1Name}
                </h4>
                {uploadedFiles1.length > 0 ? (
                  <div className="space-y-2">
                    {uploadedFiles1.map((file, index) => (
                      <div key={index} className="bg-gray-700 rounded-lg p-3">
                        <div className="flex justify-between items-start">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-mono text-gray-300 truncate">
                              {file.name}
                            </p>
                            <p className="text-xs text-gray-400">{file.size}</p>
                            <p className="text-xs">
                              {file.error ? (
                                <span className="text-red-400">
                                  {file.error}
                                </span>
                              ) : (
                                <span className="text-green-400">
                                  Processed
                                </span>
                              )}
                            </p>
                          </div>
                          <button
                            onClick={() => removeFile(index, 1)}
                            className="ml-2 text-red-400 hover:text-red-300 transition-colors text-sm"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No files uploaded</p>
                )}
              </div>

              {/* Dataset 2 Files */}
              <div>
                <h4 className="text-md font-medium text-red-400 mb-3">
                  {dataset2Name}
                </h4>
                {uploadedFiles2.length > 0 ? (
                  <div className="space-y-2">
                    {uploadedFiles2.map((file, index) => (
                      <div key={index} className="bg-gray-700 rounded-lg p-3">
                        <div className="flex justify-between items-start">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-mono text-gray-300 truncate">
                              {file.name}
                            </p>
                            <p className="text-xs text-gray-400">{file.size}</p>
                            <p className="text-xs">
                              {file.error ? (
                                <span className="text-red-400">
                                  {file.error}
                                </span>
                              ) : (
                                <span className="text-green-400">
                                  Processed
                                </span>
                              )}
                            </p>
                          </div>
                          <button
                            onClick={() => removeFile(index, 2)}
                            className="ml-2 text-red-400 hover:text-red-300 transition-colors text-sm"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No files uploaded</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showNamePrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
            <form onSubmit={handleSaveImage} className="space-y-4">
              <h3 className="text-lg font-medium text-gray-200">
                Enter Map Name
              </h3>
              <input
                type="text"
                value={mapName}
                onChange={(e) => setMapName(e.target.value)}
                placeholder="Enter map name..."
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowNamePrompt(false);
                    setMapName("");
                  }}
                  className="px-4 py-2 bg-gray-700 text-gray-300 rounded-md hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!mapName.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {chartData ? (
        <div className="space-y-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:flex-wrap">
              <label className="text-sm text-gray-300">Max Session Time:</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={maxSessionTime}
                  onChange={(e) =>
                    setMaxSessionTime(
                      Math.max(1, parseInt(e.target.value) || 90)
                    )
                  }
                  min="1"
                  max="1440"
                  className="w-20 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-400">minutes</span>
              </div>
              <div className="flex items-center gap-1">
                {[30, 60, 90, 120].map((time) => (
                  <button
                    key={time}
                    onClick={() => setMaxSessionTime(time)}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      maxSessionTime === time
                        ? "bg-blue-600 text-white"
                        : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                    }`}
                  >
                    {time}m
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 sm:max-w-sm">
                Sessions beyond this time will be combined into this value
              </p>
            </div>
            <button
              onClick={handleSaveClick}
              className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              Save as Image
            </button>
          </div>

          <div
            ref={chartContainerRef}
            className="space-y-6 bg-gray-800 p-6 rounded-lg"
          >
            <Line
              data={chartData}
              options={{
                responsive: true,
                interaction: {
                  mode: "index",
                  intersect: false,
                },
                plugins: {
                  legend: {
                    position: "top",
                    labels: {
                      color: "#E5E7EB",
                      font: {
                        size: 12,
                      },
                      usePointStyle: true,
                      padding: 15,
                    },
                  },
                  title: {
                    display: true,
                    text: "Session Length Analysis - Remaining Players Over Time",
                    color: "#E5E7EB",
                    font: {
                      size: 16,
                      weight: "bold",
                    },
                  },
                  tooltip: {
                    backgroundColor: "#374151",
                    titleColor: "#E5E7EB",
                    bodyColor: "#E5E7EB",
                    padding: 12,
                    cornerRadius: 4,
                    callbacks: {
                      label: function (context) {
                        const datasetIndex = context.datasetIndex;
                        const pointIndex = context.dataIndex;

                        // Determine which dataset this point belongs to
                        const isDataset1 =
                          datasetIndex === 0 ||
                          (datasetIndex === 0 && processedData1);
                        const isDataset2 =
                          datasetIndex === 1 ||
                          (datasetIndex === 1 && processedData2);

                        let dataPoint = null;
                        let datasetName = "";

                        if (isDataset1 && processedData1) {
                          // Find the corresponding data point in dataset 1
                          const timeValue = chartData.labels[pointIndex]
                            .replace(" min", "")
                            .replace("+", "");
                          const sessionTime = parseFloat(timeValue);
                          dataPoint = processedData1.find(
                            (item) => item.sessionTime === sessionTime
                          );
                          datasetName = dataset1Name;
                        } else if (isDataset2 && processedData2) {
                          // Find the corresponding data point in dataset 2
                          const timeValue = chartData.labels[pointIndex]
                            .replace(" min", "")
                            .replace("+", "");
                          const sessionTime = parseFloat(timeValue);
                          dataPoint = processedData2.find(
                            (item) => item.sessionTime === sessionTime
                          );
                          datasetName = dataset2Name;
                        }

                        if (dataPoint) {
                          return [
                            `${datasetName}`,
                            `Remaining Players: ${context.parsed.y.toFixed(
                              1
                            )}%`,
                            `Sessions at ${
                              dataPoint.sessionTime
                            } min: ${dataPoint.sessions.toLocaleString()}`,
                            `Total Sessions: ${dataPoint.totalSessions.toLocaleString()}`,
                          ];
                        } else {
                          return [
                            `${context.dataset.label}`,
                            `Remaining Players: ${
                              context.parsed.y
                                ? context.parsed.y.toFixed(1)
                                : "N/A"
                            }%`,
                          ];
                        }
                      },
                    },
                  },
                },
                scales: {
                  y: {
                    type: "linear",
                    display: true,
                    position: "left",
                    min: 0,
                    max: 100,
                    title: {
                      display: true,
                      text: "Remaining Players (%)",
                      color: "#E5E7EB",
                    },
                    grid: {
                      color: "rgba(229, 231, 235, 0.1)",
                    },
                    ticks: {
                      color: "#E5E7EB",
                      font: {
                        size: 11,
                      },
                      callback: function (value) {
                        return value + "%";
                      },
                    },
                  },
                  x: {
                    title: {
                      display: true,
                      text: "Session Time (minutes)",
                      color: "#E5E7EB",
                    },
                    grid: {
                      color: "rgba(229, 231, 235, 0.1)",
                    },
                    ticks: {
                      color: "#E5E7EB",
                      font: {
                        size: 11,
                      },
                    },
                  },
                },
                animation: {
                  duration: 0,
                },
              }}
            />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Dataset 1 Table */}
              {processedData1 && (
                <div>
                  <h4 className="text-lg font-medium text-green-400 mb-3">
                    {dataset1Name}
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-700">
                      <thead className="bg-gray-900">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                            Time (min)
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                            Sessions
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                            Remaining (%)
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                            Net Change (%)
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-gray-800 divide-y divide-gray-700">
                        {processedData1.map((row, index) => (
                          <tr
                            key={index}
                            className={
                              index % 2 === 0 ? "bg-gray-800" : "bg-gray-750"
                            }
                          >
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                              {row.sessionTime === maxSessionTime &&
                              sessionData1 &&
                              sessionData1.some(
                                (s) => s.sessionTime > maxSessionTime
                              )
                                ? `${row.sessionTime}+`
                                : row.sessionTime}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                              {row.sessions.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                              {row.remainingPlayersPercent.toFixed(1)}%
                            </td>
                            <td
                              className={`px-4 py-3 whitespace-nowrap text-sm ${
                                row.netChange > 0
                                  ? "text-green-400"
                                  : row.netChange < 0
                                  ? "text-red-400"
                                  : "text-gray-300"
                              }`}
                            >
                              {row.netChange > 0 ? "+" : ""}
                              {row.netChange.toFixed(1)}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-900">
                        <tr>
                          <td className="px-4 py-2 text-sm font-medium text-gray-300">
                            Total
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-300">
                            {processedData1.length > 0
                              ? processedData1[0].totalSessions.toLocaleString()
                              : 0}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-300">-</td>
                          <td className="px-4 py-2 text-sm text-gray-300">-</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {/* Dataset 2 Table */}
              {processedData2 && (
                <div>
                  <h4 className="text-lg font-medium text-red-400 mb-3">
                    {dataset2Name}
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-700">
                      <thead className="bg-gray-900">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                            Time (min)
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                            Sessions
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                            Remaining (%)
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                            Net Change (%)
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-gray-800 divide-y divide-gray-700">
                        {processedData2.map((row, index) => (
                          <tr
                            key={index}
                            className={
                              index % 2 === 0 ? "bg-gray-800" : "bg-gray-750"
                            }
                          >
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                              {row.sessionTime === maxSessionTime &&
                              sessionData2 &&
                              sessionData2.some(
                                (s) => s.sessionTime > maxSessionTime
                              )
                                ? `${row.sessionTime}+`
                                : row.sessionTime}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                              {row.sessions.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                              {row.remainingPlayersPercent.toFixed(1)}%
                            </td>
                            <td
                              className={`px-4 py-3 whitespace-nowrap text-sm ${
                                row.netChange > 0
                                  ? "text-green-400"
                                  : row.netChange < 0
                                  ? "text-red-400"
                                  : "text-gray-300"
                              }`}
                            >
                              {row.netChange > 0 ? "+" : ""}
                              {row.netChange.toFixed(1)}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-900">
                        <tr>
                          <td className="px-4 py-2 text-sm font-medium text-gray-300">
                            Total
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-300">
                            {processedData2.length > 0
                              ? processedData2[0].totalSessions.toLocaleString()
                              : 0}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-300">-</td>
                          <td className="px-4 py-2 text-sm text-gray-300">-</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-gray-800 rounded-lg p-8 text-center">
          <div className="text-gray-400 mb-4">
            <svg
              className="w-16 h-16 mx-auto mb-4 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
            <h3 className="text-lg font-medium">No Session Data Available</h3>
            <p className="mt-2">
              Please upload a ZIP file containing
              session_length/session_length.csv to analyze session length and
              player retention.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionLengthAnalyzer;
